import { useMutation, useQuery } from "@apollo/client";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { Button } from "@mui/material";
import styled from "styled-components";
import EmptyState from "../../components/EmptyState/EmptyState";
import { ErrorAlert } from "../../components/ErrorAlert/ErrorAlert";
import Loader from "../../components/Loader/Loader";
import Page from "../../components/Page/Page";
import Panel from "../../components/Panel/Panel";
import Badge from "../../components/Styled/Badge";
import { Row } from "../../components/Styled/Container";
import {
  MediaCard,
  MediaFrame,
  MediaGrid,
  MediaImage,
} from "../../components/Styled/Media";
import { StyledLink } from "../../components/Styled/StyledLink";
import { Post, PostReviewStatus } from "../../graphql/graphql";
import { REVIEW_POST } from "../../graphql/mutations-gql";
import { PUBLIC_POSTS_PENDING_REVIEW } from "../../graphql/queries-gql";

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  font-size: 12.5px;
  color: ${(props) => props.theme.secondaryTextColor};
`;

const Mono = styled.span`
  font-family: ${(props) => props.theme.monoFontFamily};
  font-size: 12px;
`;

const PostText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${(props) => props.theme.textColor};
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const PostReview = () => {
  const { data, loading, error } = useQuery(PUBLIC_POSTS_PENDING_REVIEW);
  const [reviewPost] = useMutation(REVIEW_POST);

  const posts: Post[] = data?.publicPostsPendingReview || [];

  const showAttachment = (src: string) => {
    //window.location.href = src;
    window.open(src, "_blank");
  };

  const handleApprove = (reviewStatus: PostReviewStatus, postId: string) => {
    reviewPost({
      variables: {
        postId,
        reviewStatus,
      },
      onCompleted: (data) => {
        console.log("reviewPost onCompleted", data);
      },
      onError: (error) => {
        console.log("reviewPost onError", error);
      },
      refetchQueries: [PUBLIC_POSTS_PENDING_REVIEW],
    });
  };

  return (
    <Page
      title="Public posts"
      description="Public posts waiting for review."
      maxWidth={960}
      badge={
        posts.length > 0 && <Badge $tone="primary">{posts.length} waiting</Badge>
      }
    >
      {loading && (
        <Row justifyContent="center">
          <Loader />
        </Row>
      )}
      {error && <ErrorAlert error={error} />}

      {!loading && !error && posts.length === 0 && (
        <EmptyState
          Icon={DocumentTextIcon}
          title="No posts to review"
          description="New public posts show up here."
        />
      )}

      {posts.map((post) => {
        return (
          <Panel
            key={post._id}
            title="Post"
            description={<Mono>{post._id}</Mono>}
            actions={
              <Row colGap={8}>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<XMarkIcon width={15} />}
                  onClick={() =>
                    handleApprove(PostReviewStatus.Rejected, post._id)
                  }
                >
                  Reject
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<CheckIcon width={15} />}
                  onClick={() =>
                    handleApprove(PostReviewStatus.Approved, post._id)
                  }
                >
                  Approve
                </Button>
              </Row>
            }
          >
            <Body>
              <Meta>
                <span>
                  By user{" "}
                  <StyledLink to={`/user/${post.byUserId}`}>
                    <Mono>{post.byUserId}</Mono>
                  </StyledLink>
                </span>
              </Meta>

              {post.text && <PostText>{post.text}</PostText>}

              {post.attachments && post.attachments.length > 0 && (
                <MediaGrid $min={140} $gap={8}>
                  {post.attachments.map((attachment) => {
                    return (
                      <MediaCard key={attachment.uploadId}>
                        <MediaFrame
                          title="Open attachment"
                          onClick={() => showAttachment(attachment.url ?? "")}
                        >
                          <MediaImage
                            src={attachment.thumbnailUrl ?? ""}
                            alt=""
                            loading="lazy"
                          />
                        </MediaFrame>
                      </MediaCard>
                    );
                  })}
                </MediaGrid>
              )}
            </Body>
          </Panel>
        );
      })}
    </Page>
  );
};
export default PostReview;
